import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { stcBinService } from '../../../../../services/stcBinService';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { ScanTextInput } from '../../../../common/inputs';

interface LocationOverrideModalProps {
  isOpen: boolean;
  expectedLocation: string;
  overrideLocation: string;
  companyNo: string;
  warehouseID: string;
  itemNo: string;
  requiredQuantity: number;
  onLocationChange: (value: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

export const LocationOverrideModal: React.FC<LocationOverrideModalProps> = ({
  isOpen,
  expectedLocation,
  overrideLocation,
  companyNo,
  warehouseID,
  itemNo,
  requiredQuantity,
  onLocationChange,
  onConfirm,
  onClose
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!overrideLocation.trim()) {
      setValidationError('Lokasjon kan ikke være tom');
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      // Validate location exists in database
      const locationExists = await stcBinService.exists(
        companyNo,
        warehouseID,
        overrideLocation.trim()
      );

      if (!locationExists) {
        setValidationError(`Lokasjon "${overrideLocation}" finnes ikke i lageret`);
        setIsValidating(false);
        return;
      }

      // Validate that item has sufficient inventory on the location
      const itemBins = await dataInspectionService.getItemBinsByBinCode(overrideLocation.trim());
      const itemOnLocation = itemBins.find(ib => ib.itemNo.toLowerCase() === itemNo.toLowerCase());

      if (!itemOnLocation || itemOnLocation.quantityAvailable < requiredQuantity) {
        const availableQty = itemOnLocation?.quantityAvailable || 0;
        setValidationError(`Vare "${itemNo}" har kun ${availableQty} tilgjengelig på lokasjon "${overrideLocation}", men ${requiredQuantity} er påkrevd`);
        setIsValidating(false);
        return;
      }

      // Location and inventory are valid, proceed with confirmation
      onConfirm();
    } catch (error) {
      console.error('Error validating location:', error);
      setValidationError('Kunne ikke validere lokasjon. Prøv igjen.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isValidating) {
      handleConfirm();
    }
  };

  const handleLocationChange = (value: string) => {
    setValidationError(null);
    onLocationChange(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg p-4 sm:p-6 sm:max-w-md overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Overstyr lokasjon</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            disabled={isValidating}
          >
            <X size={24} />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Opprinnelig lokasjon: <span className="font-medium">{expectedLocation}</span>
        </p>

        <ScanTextInput
          placeholder="Ny lokasjon"
          className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 mb-2 min-h-[44px] ${
            validationError ? 'border-red-500' : 'border-gray-300'
          }`}
          value={overrideLocation}
          onChange={(e) => handleLocationChange(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isValidating}
          autoFocus
        />

        {validationError && (
          <div className="flex items-start gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleConfirm}
            disabled={isValidating || !overrideLocation.trim()}
            className="flex-1 bg-blue-600 text-white py-3 sm:py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px] order-1"
          >
            {isValidating ? 'Validerer...' : 'Bekreft'}
          </button>
          <button
            onClick={onClose}
            disabled={isValidating}
            className="px-4 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px] order-2"
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
};